import React, { useState, useEffect } from "react";
import "./DummyConfig.css";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from "@mui/material/Snackbar";
import { styled } from "@mui/material/styles";
import Loader from "../../Components/Loader/Loader";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.aeroprime.in/crm-service/admin";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#334555",
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: 13,
  },
}));

const RedSwitch = styled(Switch)(() => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#ef5443",
    "&:hover": { backgroundColor: "rgba(239,84,67,0.08)" },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#ef5443",
  },
}));

const TRIP_TYPES = ["ONE_WAY", "ROUND_TRIP", "MULTI_CITY"];
const CONN_TYPES = ["", "DIRECT", "CONNECTING"];

const DummyConfig = () => {
  const navigate = useNavigate();

  const [showLoader, setShowLoader] = useState(false);

  // Airlines state
  const [airlines, setAirlines] = useState([]);
  const [showAddAirline, setShowAddAirline] = useState(false);
  const [newAirlineCode, setNewAirlineCode] = useState("");
  const [newAirlineDesc, setNewAirlineDesc] = useState("");
  const [newAirlineEnabled, setNewAirlineEnabled] = useState(false);

  // PNRs state
  const [pnrs, setPnrs] = useState([]);
  const [pnrAirlineFilter, setPnrAirlineFilter] = useState("");
  const [showAddPnr, setShowAddPnr] = useState(false);
  const [newPnr, setNewPnr] = useState({
    airlineCode: "",
    pnrNumber: "",
    referenceId: "",
    tripType: "ONE_WAY",
    paxTypeSummary: "",
    connectionType: "",
    origin: "",
    destination: "",
    description: "",
  });

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("AuthToken"),
  });

  const handle401 = (err) => {
    if (err?.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/";
    }
  };

  const showSnack = (msg, type = "success") =>
    setSnack({ open: true, msg, type });

  // ---- Airlines ----

  const fetchAirlines = () => {
    setShowLoader(true);
    axios
      .get(`${BASE_URL}/dummy/airlines`, { headers: getHeaders() })
      .then((res) => {
        setAirlines(res.data || []);
        setShowLoader(false);
      })
      .catch((err) => {
        setShowLoader(false);
        handle401(err);
        showSnack("Failed to load airline configs.", "error");
      });
  };

  const toggleAirline = (airlineCode) => {
    axios
      .put(`${BASE_URL}/dummy/airline/${airlineCode}/toggle`, {}, { headers: getHeaders() })
      .then((res) => {
        showSnack(res.data?.message || "Updated.");
        fetchAirlines();
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to toggle airline.", "error");
      });
  };

  const saveAirline = () => {
    if (!newAirlineCode.trim()) {
      showSnack("Airline code is required.", "error");
      return;
    }
    axios
      .post(
        `${BASE_URL}/dummy/airline`,
        {
          airlineCode: newAirlineCode.trim().toUpperCase(),
          description: newAirlineDesc.trim() || null,
          isEnabled: newAirlineEnabled,
        },
        { headers: getHeaders() }
      )
      .then((res) => {
        if (res.data?.success) {
          showSnack(res.data.message || "Saved.");
          setShowAddAirline(false);
          setNewAirlineCode("");
          setNewAirlineDesc("");
          setNewAirlineEnabled(false);
          fetchAirlines();
        } else {
          showSnack(res.data?.message || "Failed.", "error");
        }
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to save airline.", "error");
      });
  };

  // ---- PNRs ----

  const fetchPnrs = (airlineCode) => {
    const url = airlineCode
      ? `${BASE_URL}/dummy/pnrs?airlineCode=${encodeURIComponent(airlineCode)}`
      : `${BASE_URL}/dummy/pnrs`;
    axios
      .get(url, { headers: getHeaders() })
      .then((res) => setPnrs(res.data || []))
      .catch((err) => {
        handle401(err);
        showSnack("Failed to load PNR configs.", "error");
      });
  };

  const togglePnr = (id) => {
    axios
      .put(`${BASE_URL}/dummy/pnr/${id}/toggle`, {}, { headers: getHeaders() })
      .then((res) => {
        showSnack(res.data?.message || "Updated.");
        fetchPnrs(pnrAirlineFilter);
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to toggle PNR.", "error");
      });
  };

  const deletePnr = (id, pnrNumber) => {
    if (!window.confirm(`Delete PNR config for "${pnrNumber}"? This cannot be undone.`)) return;
    axios
      .delete(`${BASE_URL}/dummy/pnr/${id}`, { headers: getHeaders() })
      .then((res) => {
        showSnack(res.data?.message || "Deleted.");
        fetchPnrs(pnrAirlineFilter);
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to delete PNR.", "error");
      });
  };

  const savePnr = () => {
    if (!newPnr.airlineCode.trim()) { showSnack("Airline code is required.", "error"); return; }
    if (!newPnr.pnrNumber.trim())   { showSnack("PNR number is required.", "error"); return; }
    if (!newPnr.paxTypeSummary.trim()) { showSnack("Pax type summary is required.", "error"); return; }

    const payload = {
      airlineCode: newPnr.airlineCode.trim().toUpperCase(),
      pnrNumber:   newPnr.pnrNumber.trim().toUpperCase(),
      tripType:    newPnr.tripType,
      paxTypeSummary: newPnr.paxTypeSummary.trim(),
      description: newPnr.description.trim() || null,
      isActive:    true,
    };
    if (newPnr.referenceId) payload.referenceId = parseInt(newPnr.referenceId, 10);
    if (newPnr.connectionType) payload.connectionType = newPnr.connectionType;
    if (newPnr.origin) payload.origin = newPnr.origin.trim().toUpperCase();
    if (newPnr.destination) payload.destination = newPnr.destination.trim().toUpperCase();

    axios
      .post(`${BASE_URL}/dummy/pnr`, payload, { headers: getHeaders() })
      .then((res) => {
        if (res.data?.success) {
          showSnack(res.data.message || "Saved.");
          setShowAddPnr(false);
          setNewPnr({ airlineCode: pnrAirlineFilter, pnrNumber: "", referenceId: "", tripType: "ONE_WAY", paxTypeSummary: "", connectionType: "", origin: "", destination: "", description: "" });
          fetchPnrs(pnrAirlineFilter);
        } else {
          showSnack(res.data?.message || "Failed.", "error");
        }
      })
      .catch((err) => {
        handle401(err);
        showSnack("Failed to save PNR.", "error");
      });
  };

  useEffect(() => {
    fetchAirlines();
    fetchPnrs("");
  }, []);

  const handleFilterChange = (e) => {
    const val = e.target.value;
    setPnrAirlineFilter(val);
    fetchPnrs(val);
  };

  const handleShowAddPnr = () => {
    setNewPnr((p) => ({ ...p, airlineCode: pnrAirlineFilter || "" }));
    setShowAddPnr((v) => !v);
  };

  const tripTypeBadgeClass = (t) => {
    if (t === "ONE_WAY") return "dc-badge dc-badge-oneway";
    if (t === "ROUND_TRIP") return "dc-badge dc-badge-roundtrip";
    return "dc-badge dc-badge-multicty";
  };

  const connTypeBadgeClass = (c) => {
    if (c === "DIRECT") return "dc-badge dc-badge-direct";
    if (c === "CONNECTING") return "dc-badge dc-badge-connecting";
    return "dc-badge";
  };

  return (
    <div className="dummy-config-wrapper">
      {showLoader && <Loader hideLoader={false} />}

      <div className="dummy-config-heading">DUMMY TESTING CONFIG</div>
      <div className="Break-line-dc" />

      {/* ---- Airlines Section ---- */}
      <div className="dc-section-block">
        <div className="dc-section-header">
          <span className="dc-section-title">Dummy Airlines</span>
          <button className="dc-add-btn" onClick={() => setShowAddAirline((v) => !v)}>
            + Add Airline
          </button>
        </div>

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Airline Code</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Dummy Mode</StyledTableCell>
                <StyledTableCell>Updated At</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {airlines.length > 0 ? (
                airlines.map((a) => (
                  <TableRow key={a.airlineCode} hover>
                    <TableCell className="dc-airline-mono">{a.airlineCode}</TableCell>
                    <TableCell style={{ color: "#666", fontSize: 13 }}>{a.description || "—"}</TableCell>
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <RedSwitch
                          size="small"
                          checked={!!a.isEnabled}
                          onChange={() => toggleAirline(a.airlineCode)}
                        />
                        <span className={`dc-badge ${a.isEnabled ? "dc-badge-enabled" : "dc-badge-disabled"}`}>
                          {a.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell style={{ color: "#888", fontSize: 12 }}>
                      {a.updatedAt ? new Date(a.updatedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className="dc-empty">No airline configs found.</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {showAddAirline && (
          <div className="dc-add-form">
            <h4>Add / Update Airline</h4>
            <div className="dc-form-grid">
              <div className="dc-form-group">
                <label>Airline Code *</label>
                <input
                  className="dc-form-input mono"
                  placeholder="e.g. C6"
                  maxLength={10}
                  value={newAirlineCode}
                  onChange={(e) => setNewAirlineCode(e.target.value)}
                />
              </div>
              <div className="dc-form-group">
                <label>Description</label>
                <input
                  className="dc-form-input"
                  placeholder="Optional note"
                  value={newAirlineDesc}
                  onChange={(e) => setNewAirlineDesc(e.target.value)}
                />
              </div>
              <div className="dc-form-group">
                <label>Dummy Mode</label>
                <select
                  className="dc-form-select"
                  value={newAirlineEnabled ? "true" : "false"}
                  onChange={(e) => setNewAirlineEnabled(e.target.value === "true")}
                >
                  <option value="false">Disabled</option>
                  <option value="true">Enabled</option>
                </select>
              </div>
            </div>
            <div className="dc-form-actions">
              <button className="dc-save-btn" onClick={saveAirline}>Save</button>
              <button className="dc-cancel-btn" onClick={() => setShowAddAirline(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ---- PNR Section ---- */}
      <div className="dc-section-block">
        <div className="dc-section-header">
          <span className="dc-section-title">Dummy PNR Configs</span>
          <button className="dc-add-btn" onClick={handleShowAddPnr}>
            + Add PNR
          </button>
        </div>

        <div className="dc-filter-row">
          <span className="dc-filter-label">Filter by Airline:</span>
          <select className="dc-filter-select" value={pnrAirlineFilter} onChange={handleFilterChange}>
            <option value="">All Airlines</option>
            {airlines.map((a) => (
              <option key={a.airlineCode} value={a.airlineCode}>{a.airlineCode}</option>
            ))}
          </select>
        </div>

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>Airline</StyledTableCell>
                <StyledTableCell>PNR</StyledTableCell>
                <StyledTableCell>Ref ID</StyledTableCell>
                <StyledTableCell>Trip Type</StyledTableCell>
                <StyledTableCell>Pax Summary</StyledTableCell>
                <StyledTableCell>Connection</StyledTableCell>
                <StyledTableCell>Origin</StyledTableCell>
                <StyledTableCell>Destination</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Active</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pnrs.length > 0 ? (
                pnrs.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell style={{ color: "#999", fontSize: 12 }}>{p.id}</TableCell>
                    <TableCell>
                      <span className="dc-badge dc-badge-airline">{p.airlineCode}</span>
                    </TableCell>
                    <TableCell className="dc-pnr-mono">{p.pnrNumber}</TableCell>
                    <TableCell style={{ color: "#888", fontSize: 12 }}>{p.referenceId ?? "—"}</TableCell>
                    <TableCell>
                      <span className={tripTypeBadgeClass(p.tripType)}>{p.tripType || "—"}</span>
                    </TableCell>
                    <TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>{p.paxTypeSummary || "—"}</TableCell>
                    <TableCell>
                      <span className={connTypeBadgeClass(p.connectionType)}>{p.connectionType || "ANY"}</span>
                    </TableCell>
                    <TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>{p.origin || "—"}</TableCell>
                    <TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>{p.destination || "—"}</TableCell>
                    <TableCell
                      style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "#666" }}
                      title={p.description || ""}
                    >
                      {p.description || "—"}
                    </TableCell>
                    <TableCell>
                      <RedSwitch
                        size="small"
                        checked={!!p.isActive}
                        onChange={() => togglePnr(p.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => deletePnr(p.id, p.pnrNumber)}>
                        <DeleteIcon fontSize="small" style={{ color: "#ef5443" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12}>
                    <div className="dc-empty">No PNR configs found.</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {showAddPnr && (
          <div className="dc-add-form">
            <h4>Add PNR Config</h4>
            <div className="dc-form-grid">
              <div className="dc-form-group">
                <label>Airline Code *</label>
                <input
                  className="dc-form-input mono"
                  placeholder="e.g. C6"
                  maxLength={10}
                  value={newPnr.airlineCode}
                  onChange={(e) => setNewPnr((p) => ({ ...p, airlineCode: e.target.value }))}
                />
              </div>
              <div className="dc-form-group">
                <label>PNR Number *</label>
                <input
                  className="dc-form-input mono"
                  placeholder="e.g. ABKOEM"
                  maxLength={50}
                  value={newPnr.pnrNumber}
                  onChange={(e) => setNewPnr((p) => ({ ...p, pnrNumber: e.target.value }))}
                />
              </div>
              <div className="dc-form-group">
                <label>Reference ID</label>
                <input
                  className="dc-form-input mono"
                  placeholder="Optional int"
                  type="number"
                  value={newPnr.referenceId}
                  onChange={(e) => setNewPnr((p) => ({ ...p, referenceId: e.target.value }))}
                />
              </div>
              <div className="dc-form-group">
                <label>Trip Type *</label>
                <select
                  className="dc-form-select"
                  value={newPnr.tripType}
                  onChange={(e) => setNewPnr((p) => ({ ...p, tripType: e.target.value }))}
                >
                  {TRIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="dc-form-group">
                <label>Pax Type Summary *</label>
                <input
                  className="dc-form-input mono"
                  placeholder="e.g. 1ADT, 2ADT1CHD"
                  value={newPnr.paxTypeSummary}
                  onChange={(e) => setNewPnr((p) => ({ ...p, paxTypeSummary: e.target.value }))}
                />
              </div>
              <div className="dc-form-group">
                <label>Connection Type</label>
                <select
                  className="dc-form-select"
                  value={newPnr.connectionType}
                  onChange={(e) => setNewPnr((p) => ({ ...p, connectionType: e.target.value }))}
                >
                  {CONN_TYPES.map((c) => <option key={c} value={c}>{c || "Any"}</option>)}
                </select>
              </div>
              <div className="dc-form-group">
                <label>Origin</label>
                <input
                  className="dc-form-input mono"
                  placeholder="e.g. DEL"
                  maxLength={10}
                  value={newPnr.origin}
                  onChange={(e) => setNewPnr((p) => ({ ...p, origin: e.target.value }))}
                />
              </div>
              <div className="dc-form-group">
                <label>Destination</label>
                <input
                  className="dc-form-input mono"
                  placeholder="e.g. BOM"
                  maxLength={10}
                  value={newPnr.destination}
                  onChange={(e) => setNewPnr((p) => ({ ...p, destination: e.target.value }))}
                />
              </div>
              <div className="dc-form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Description</label>
                <input
                  className="dc-form-input"
                  placeholder="e.g. 1 Adult Booking — one-way direct"
                  value={newPnr.description}
                  onChange={(e) => setNewPnr((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="dc-form-actions">
              <button className="dc-save-btn" onClick={savePnr}>Save</button>
              <button className="dc-cancel-btn" onClick={() => setShowAddPnr(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.msg}
        ContentProps={{
          className: snack.type === "error" ? "dc-snack-error" : "dc-snack-success",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
};

export default DummyConfig;
